/**
 * cbamFormulas.js — Central CBAM Formula Engine
 */


//  Safe number parser
const p = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round = (x, d = 6) => {
  if (!Number.isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * Math.pow(10, d)) / Math.pow(10, d);
};

//  CONSTANTS
// Source: IPCC AR5, EU MRR, CBAM Implementing Regulation Annex III

export const CONSTANTS = {
  // Global Warming Potential values (IPCC AR5)
  GWP_CF4:  6630,    // tCO2e per t CF4
  GWP_C2F6: 11100,   // tCO2e per t C2F6
  GWP_N2O:  265,     // tCO2e per t N2O

  // Molecular weight ratio CO2/C
  CO2_C_RATIO: 3.664,   // = 3.664 tCO2 per tC

  // Energy conversion
  MWH_TO_TJ: 0.0036,      // 1 MWh = 0.0036 TJ
  GJ_TO_TJ: 0.001,         // 1 GJ = 0.001 TJ

  // CHP default attribution factors (EU MRR Article 22)
  CHP_HEAT_DEFAULT_EFF:  0.88,   // 88% reference heat efficiency
  CHP_ELEC_DEFAULT_EFF:  0.442,  // 44.2% reference electricity efficiency
  CHP_HEAT_ATTR_DEFAULT: 0.55,   // 55% emissions attributed to heat (default)
  CHP_ELEC_ATTR_DEFAULT: 0.25,   // 25% emissions attributed to electricity (default)

  // Waste gas export energy ratio (Formula D.11)
  WASTE_GAS_EXPORT_RATIO: 0.667,

  // EU default emission factors (tCO2/TJ) — IPCC 2006, verified
  EU_DEFAULT_EF: {
    'Natural Gas': 56.1,
    'Coal':        94.6,
    'Oil':         73.3,
    'Biomass':     0,
    'Waste':       91.7,
  },

  // NCV unit mapping (Formula B.1)
  NCV_UNIT: {
    kNm3: 'GJ/kNm3',
    t:    'GJ/t',
  },

  // Data quality options (Formula C.4)
  DATA_QUALITY_LEVELS: [
    'Mostly measurements & analyses',
    'Mix of measurements and estimates',
    'Mostly estimates',
    'Default values only',
  ],

  DATA_VERIFICATION_METHODS: [
    'Internal audits',
    'External verification',
    'No verification',
  ],
};

// FORMULA NAMESPACE B — Sheet B_EmInst

export const B = {

  /**
   * B.1 — NCV Unit Auto-Selector
   * If AD unit is kNm3 → NCV unit is GJ/kNm3, else GJ/t
   * @param {string} adUnit - Activity data unit
   * @returns {string} NCV unit
   */
  nvcUnitSelector(adUnit) {
    return adUnit === 'kNm3' ? 'GJ/kNm3' : 'GJ/t';
  },

  /**
   * B.2 + B.3 + B.4 + B.7 — Combustion Approach
   * Formula: fossilCo2 = AD × (NCV/1000) × EF × OxF × (1 - bioFrac)
   * bioCo2   = AD × (NCV/1000) × EF × OxF × bioFrac
   * energyFossilTJ = AD × (NCV/1000) × (1 - bioFrac)
   * energyBioTJ    = AD × (NCV/1000) × bioFrac
   *
   * @param {object} inputs
   * @param {number} inputs.activityData - AD quantity (t or kNm3)
   * @param {number} inputs.ncv          - Net calorific value (GJ/t or GJ/kNm3)
   * @param {number} inputs.ef           - Emission factor (tCO2/TJ)
   * @param {number} inputs.oxf          - Oxidation factor (% or fraction, auto-normalised)
   * @param {number} inputs.biomassContent - Biomass fraction (% or fraction, auto-normalised)
   * @returns {{ fossilCo2, bioCo2, energyGJ, energyFossilTJ, energyBioTJ }}
   */
    combustion({ activityData, ncv, ef, efUnit = 'tCO2/TJ', oxf = 100, biomassContent = 0 }) {     // Normalise % inputs to fractions
    const bioFrac = p(biomassContent) > 1 ? p(biomassContent) / 100 : p(biomassContent);
    const oxfFrac = p(oxf) > 1           ? p(oxf) / 100           : (p(oxf) || 1);

    const energyGJ       = p(activityData) * p(ncv);               // GJ
    const energyTJ       = energyGJ * CONSTANTS.GJ_TO_TJ;          // TJ (÷1000)

    // B.4: Fossil CO2
    // B.4: Fossil CO2 — mass-based EF (tCO2/t) uses AD×EF directly; energy-based uses the energy path
    const co2gross  = (efUnit === 'tCO2/t')
      ? p(activityData) * p(ef)
      : energyTJ * p(ef);                         // tCO2 (EF in tCO2/TJ)
    const co2adj    = co2gross * oxfFrac;                           // apply oxidation factor
    const fossilCo2 = co2adj * (1 - bioFrac);                       // B.4
    const bioCo2    = co2adj * bioFrac;                             // B.7

    // B.2 + B.3: Energy split
    const energyFossilTJ = energyTJ * (1 - bioFrac);               // B.2
    const energyBioTJ    = energyTJ * bioFrac;                      // B.3

    return {
      fossilCo2:    round(fossilCo2),
      bioCo2:       round(bioCo2),
      energyGJ:     round(energyGJ),
      energyFossilTJ: round(energyFossilTJ),
      energyBioTJ:  round(energyBioTJ),
    };
  },

  /**
   * B.5 — Process Emissions Approach (e.g. limestone calcination)
   * Formula: fossilCo2 = AD × EF × (1 - bioFrac)
   * Note: EF is in tCO2/t (mass-based), no NCV needed
   *
   * @param {object} inputs
   * @param {number} inputs.activityData   - Material quantity (t)
   * @param {number} inputs.ef             - Emission factor (tCO2/t)
   * @param {number} inputs.biomassContent - Biomass fraction (% or fraction)
   * @returns {{ fossilCo2, bioCo2, energyFossilTJ, energyBioTJ }}
   */
  processEmissions({ activityData, ef, biomassContent = 0 }) {
    const bioFrac   = p(biomassContent) > 1 ? p(biomassContent) / 100 : p(biomassContent);
    const fossilCo2 = p(activityData) * p(ef) * (1 - bioFrac);     // B.5 (fixed: was missing bio)
    const bioCo2    = p(activityData) * p(ef) * bioFrac;

    return {
      fossilCo2:    round(fossilCo2),
      bioCo2:       round(bioCo2),
      energyGJ:     0,
      energyFossilTJ: 0,
      energyBioTJ:  0,
    };
  },

  /**
   * B.6 + B.8 — Mass Balance Approach
   * Formula: fossilCo2 = AD × carbonContent × 3.6667 × (1 - bioFrac)
   * bioCo2           = AD × carbonContent × 3.6667 × bioFrac        (B.8)
   *
   * @param {object} inputs
   * @param {number} inputs.activityData   - Material quantity (t)
   * @param {number} inputs.carbonContent  - Carbon content (tC/t)
   * @param {number} inputs.biomassContent - Biomass fraction (% or fraction)
   * @returns {{ fossilCo2, bioCo2, energyFossilTJ, energyBioTJ }}
   */
  massBalance({ activityData, carbonContent, biomassContent = 0 }) {
    const bioFrac   = p(biomassContent) > 1 ? p(biomassContent) / 100 : p(biomassContent);
    const totalCo2  = p(activityData) * p(carbonContent) * CONSTANTS.CO2_C_RATIO; // 44/12 = 3.6667
    const fossilCo2 = totalCo2 * (1 - bioFrac);                    // B.6
    const bioCo2    = totalCo2 * bioFrac;                           // B.8 (was 0 — FIXED)

    return {
      fossilCo2:    round(fossilCo2),
      bioCo2:       round(bioCo2),
      energyGJ:     0,
      energyFossilTJ: 0,
      energyBioTJ:  0,
    };
  },

  /**
   * B.9 — Unit Consistency Check
   * Verifies AD unit is compatible with EF unit
   * Returns null if consistent, error string if not
   *
   * @param {string} adUnit - Activity data unit (t, kNm3)
   * @param {string} efUnit - Emission factor unit (tCO2/TJ, tCO2/t)
   * @param {string} method - Calculation method
   * @returns {string|null} null = consistent, string = error message
   */
  unitConsistencyCheck(adUnit, efUnit, method) {
    const m = (method || '').toLowerCase();
    if (!adUnit || !efUnit) return null; // Can't check yet

    // Mass-based EF (process emissions, mass balance): must use tCO2/t
    if (m === 'process emissions' || m === 'mass balance') {
      if (efUnit !== 'tCO2/t') return 'Inconsistent: Process/MassBalance methods require EF in tCO2/t';
      return null;
    }

    // Combustion: energy-based EF (tCO2/TJ) requires NCV; mass-based (tCO2/t) does not
    if (m === 'combustion') {
      const valid = efUnit === 'tCO2/TJ' || efUnit === 'tCO2/t';
      if (!valid) return 'Inconsistent: Combustion requires EF in tCO2/TJ or tCO2/t';
      return null;
    }

    return null;
  },

  /**
   * B.10 — Completeness Check per Row
   * Returns array of missing fields, empty array = complete
   *
   * @param {object} row - Source row data
   * @returns {string[]} Array of missing field names
   */
  completenessCheck(row) {
    const missing = [];
    const m = (row.method || '').toLowerCase();

    if (!row.sourceName)    missing.push('Source stream name');
    if (!row.activityData)  missing.push('Activity data');
    if (!row.adUnit)        missing.push('AD unit');
    if (!row.ef)            missing.push('Emission factor');
    if (!row.efUnit)        missing.push('EF unit');

    // NCV only needed for the energy path (tCO2/TJ); mass-based combustion (tCO2/t) doesn't use NCV
    if (m === 'combustion' && row.efUnit !== 'tCO2/t') {
      if (!row.ncv)         missing.push('NCV');
      if (!row.ncvUnit)     missing.push('NCV unit');
    }
    if (m === 'mass balance') {
      if (!row.carbonContent) missing.push('Carbon content');
    }

    return missing;
  },

  /**
   * B.11 — Total Fossil CO2 with Validation Gate
   * Three-level gate: inconsistent → incomplete → valid sum
   *
   * @param {object} row - Source row after recalculation
   * @returns {{ value: number|null, status: 'ok'|'inconsistent'|'incomplete'|'empty', message: string }}
   */
  totalFossilCO2WithGate(row) {
    if (!row.method) return { value: null, status: 'empty', message: '' };

    const unitError = B.unitConsistencyCheck(row.adUnit, row.efUnit, row.method);
    if (unitError) return { value: null, status: 'inconsistent', message: unitError };

    const missing = B.completenessCheck(row);
    if (missing.length > 0) return {
      value: null, status: 'incomplete',
      message: `Incomplete: missing ${missing.join(', ')}`
    };

    return { value: p(row.co2eFossil), status: 'ok', message: '' };
  },

  /**
   * B.13 — PFC CF4 Slope Method
   * Formula: CF4 = AD × (Freq × Duration) × SlopeCF4 / 1000
   * AEM (Anode Effect Minutes) = Frequency × Duration
   *
   * @param {object} inputs
   * @returns {{ cf4, c2f6, cf4Co2e, c2f6Co2e, fossilCo2 }}
   */
  pfcSlope({ activityData, frequency, duration, sefCF4, fC2F6, gwpCF4, gwpC2F6, collectionEfficiency }) {
    const AEM  = p(frequency) * p(duration);                        // anode effect minutes/cell-day
    const cf4  = p(activityData) * AEM * p(sefCF4) / 1000;         // B.13: t CF4
    const c2f6 = cf4 * p(fC2F6);                                    // B.16: t C2F6

    const cf4Co2e  = cf4  * p(gwpCF4  || CONSTANTS.GWP_CF4);       // B.17
    const c2f6Co2e = c2f6 * p(gwpC2F6 || CONSTANTS.GWP_C2F6);      // B.18

    const collEff   = p(collectionEfficiency) > 1
      ? p(collectionEfficiency) / 100
      : (p(collectionEfficiency) || 1);

    // B.19: DIVIDE by collection efficiency (was × — FIXED)
    const fossilCo2 = (cf4Co2e + c2f6Co2e) / collEff;

    return {
      cf4:        round(cf4),
      c2f6:       round(c2f6),
      cf4Co2e:    round(cf4Co2e),
      c2f6Co2e:   round(c2f6Co2e),
      fossilCo2:  round(fossilCo2),
    };
  },

  /**
   * B.14 — PFC CF4 Overvoltage Method (FIXED)
   * Formula: CF4 = AD × (Overvoltage / CurrentEfficiency) × OVC / 1000
   * Was: aeo × ce × ovc × AD — WRONG ORDER
   *
   * @param {object} inputs
   * @returns {{ cf4, c2f6, cf4Co2e, c2f6Co2e, fossilCo2 }}
   */
  pfcOvervoltage({ activityData, aeo, ce, ovc, fC2F6, gwpCF4, gwpC2F6, collectionEfficiency }) {
    // B.14 FIXED: AD × (aeo/ce) × ovc / 1000
    const cf4  = p(activityData) * (p(aeo) / (p(ce) || 1)) * p(ovc) / 1000;
    const c2f6 = cf4 * p(fC2F6);

    const cf4Co2e  = cf4  * p(gwpCF4  || CONSTANTS.GWP_CF4);
    const c2f6Co2e = c2f6 * p(gwpC2F6 || CONSTANTS.GWP_C2F6);

    const collEff   = p(collectionEfficiency) > 1
      ? p(collectionEfficiency) / 100
      : (p(collectionEfficiency) || 1);

    // B.19: DIVIDE by collection efficiency (FIXED)
    const fossilCo2 = (cf4Co2e + c2f6Co2e) / collEff;

    return {
      cf4:        round(cf4),
      c2f6:       round(c2f6),
      cf4Co2e:    round(cf4Co2e),
      c2f6Co2e:   round(c2f6Co2e),
      fossilCo2:  round(fossilCo2),
    };
  },

  /**
   * B.20 + B.21 + B.22 — CEMS Measurement-Based (FIXED)
   * Formula: annualGHG = Conc × Flow × Hours / 1000   (g/Nm3 × Nm3/h × h → t, ÷1000)
   * fossilCo2e = annualGHG × GWP × (1 - bioFrac)
   * bioCo2e    = annualGHG × GWP × bioFrac
   *
   * @param {object} inputs
   * @returns {{ annualGHG, fossilCo2, bioCo2, energyFossilTJ, energyBioTJ }}
   */
  measurement({ hourlyGHGConc, hoursOperating, flueGasFlow, gwp = 1, biomassContent = 0 }) {
    const bioFrac = p(biomassContent) > 1 ? p(biomassContent) / 100 : p(biomassContent);

    // B.20 FIXED: ÷1000 to convert g/Nm3 × kNm3/h × h → tonnes
    const annualGHG = p(hourlyGHGConc) * p(flueGasFlow) * p(hoursOperating) / 1000;
    const co2e      = annualGHG * p(gwp || 1);                     // B.21

    const fossilCo2 = co2e * (1 - bioFrac);                        // B.21
    const bioCo2    = co2e * bioFrac;                               // B.22

    // Approximate energy from fossil CO2 (for fuel balance in FormC)
    const energyFossilTJ = fossilCo2 / 1000;
    const energyBioTJ    = bioCo2    / 1000;

    return {
      annualGHG:    round(annualGHG),
      fossilCo2:    round(fossilCo2),
      bioCo2:       round(bioCo2),
      energyFossilTJ: round(energyFossilTJ),
      energyBioTJ:  round(energyBioTJ),
    };
  },

  /**
   * Master recalculator — dispatches to correct formula based on method
   * Called on every field change in FormB rows
   *
   * @param {object} row - Full source row object
   * @returns {object} Row with updated calculated fields
   */
  recalcSourceRow(row) {
    const method = (row.method || '').toLowerCase().trim();
    let result = { fossilCo2: 0, bioCo2: 0, energyGJ: 0, energyFossilTJ: 0, energyBioTJ: 0 };
    if (method === 'combustion')        result = B.combustion({ ...row, oxf: row.oxidationFactor });
    else if (method === 'mass balance') result = B.massBalance(row);
    else if (method === 'process emissions') result = B.processEmissions(row);
    else return row; // Unknown method — return unchanged

    const gate = B.totalFossilCO2WithGate({ ...row, co2eFossil: result.fossilCo2 });

    return {
      ...row,
      co2eFossil:     result.fossilCo2,
      co2eBio:        result.bioCo2,
      energyFossilTJ: result.energyFossilTJ,
      energyBioTJ:    result.energyBioTJ,
      _validationStatus:  gate.status,
      _validationMessage: gate.message,
    };
  },

  recalcPFCRow(row) {
    const method = (row.method || '').toLowerCase().trim();
    let result = { cf4: 0, c2f6: 0, cf4Co2e: 0, c2f6Co2e: 0, fossilCo2: 0 };

    if      (method === 'slope method')      result = B.pfcSlope(row);
    else if (method === 'overvoltage method') result = B.pfcOvervoltage(row);
    else return row;

    return {
      ...row,
      cf4Emissions:  result.cf4,
      c2f6Emissions: result.c2f6,
      cf4Co2e:       result.cf4Co2e,
      c2f6Co2e:      result.c2f6Co2e,
      co2eFossil:    result.fossilCo2,
    };
  },

  recalcMeasurementRow(row) {
    const result = B.measurement(row);
    return {
      ...row,
      annualAmountOfGHG: result.annualGHG,
      co2eFossil:        result.fossilCo2,
      co2eBio:           result.bioCo2,
      energyFossilTJ:    result.energyFossilTJ,
      energyBioTJ:       result.energyBioTJ,
    };
  },
};

// FORMULA NAMESPACE C — Sheet C_Emissions&Energy

export const C = {

  /**
   * C.1 — Total Fossil Energy Input from Sheet B
   * Sums energyFossilTJ across all source and measurement rows
   */
  totalFossilEnergy(sourceRows = [], measRows = []) {
    const fromSources = sourceRows.reduce((s, r) => s + p(r.energyFossilTJ), 0);
    const fromMeas    = measRows.reduce((s, r)   => s + p(r.energyFossilTJ), 0);
    return round(fromSources + fromMeas);
  },

  /**
   * C.2 — Measured Override Logic
   * Uses measured value if provided, otherwise uses calculated
   */
  measuredOverride(calculated, measured) {
    return (measured !== null && measured !== undefined && measured !== '')
      ? p(measured)
      : p(calculated);
  },

  /**
   * C.3 — GHG Totals from Form B (all sections)
   * Returns co2, biomass, pfc, direct totals for Form C GHG balance.
   *
   * The default Direct Emissions value is specifically the sum of fossil CO2
   * from Mass Balance and Process Emissions source streams. Other Form B
   * categories remain available in their respective totals, but do not change
   * this default Direct Emissions value.
   */
  ghgTotalsFromB(sourceRows = [], pfcRows = [], measRows = []) {
    const co2Fossil_a = sourceRows.reduce((s, r) => s + p(r.co2eFossil), 0);
    const co2Bio_a    = sourceRows.reduce((s, r) => s + p(r.co2eBio),    0);

    // FIX: use co2eFossil (already divided by collectionEfficiency) not cf4Co2e+c2f6Co2e (before division → wrong)
    const pfcTotal    = pfcRows.reduce((s, r) => s + p(r.co2eFossil), 0);

    const co2Fossil_c = measRows.reduce((s, r) => s + p(r.co2eFossil), 0);
    const co2Bio_c    = measRows.reduce((s, r) => s + p(r.co2eBio),    0);

    const totalCO2     = co2Fossil_a + co2Fossil_c;
    const totalBiomass = co2Bio_a    + co2Bio_c;
    const totalDirect = sourceRows.reduce((sum, row) => {
      const method = (row.method || '').trim().toLowerCase();
      return method === 'mass balance' || method === 'process emissions'
        ? sum + p(row.co2eFossil)
        : sum;
    }, 0);

    return {
      co2:         round(totalCO2),
      biomass:     round(totalBiomass),
      pfc:         round(pfcTotal),
      direct:      round(totalDirect),
      calcBased:   round(co2Fossil_a),  // section (a) total — fossil only per EU regulation
      measBased:   round(co2Fossil_c),  // section (c) total — fossil only per EU regulation
    };
  },

  /**
   * C — Indirect emissions from Form D electricity data
   * Σ (consumption × EF) - (exported × EF_exported) for applicable processes
   */
  indirectFromD(formDProcesses = []) {
    return round(formDProcesses.reduce((s, proc) => {
      if (proc.applicableElements?.indirectEmissions !== 'TRUE') return s;
      const consumed = p(proc.electricityConsumption?.consumption)
                     * p(proc.electricityConsumption?.emissionFactor);
      const exported = p(proc.electricityExported?.amountsExported)
                     * p(proc.electricityExported?.emissionFactor);
      return s + (consumed - exported);
    }, 0));
  },

  /**
   * C — Electricity TJ from Form D (for fuel balance)
   * Converts MWh to TJ: 1 MWh = 0.0036 TJ
   */
  electricityTJFromD(formDProcesses = []) {
    const totalMWh = formDProcesses.reduce(
      (s, proc) => s + p(proc.electricityConsumption?.consumption), 0
    );
    return round(totalMWh * CONSTANTS.MWH_TO_TJ);
  },
};

// FORMULA NAMESPACE D — Sheet D_Processes 

export const D = {

  /**
   * D.1 — Total Production Volume (AUTO-CALCULATED — was manual input before)
   * Sums all production level amounts across sub-rows
   * @param {Array} productionLevels - Array of { amounts: number } objects
   * @returns {number} Total production volume
   */
  totalProductionVolume(productionLevels = []) {
    return round(productionLevels.reduce((s, row) => s + p(row.amounts), 0));
  },

  /**
   * D.3 — Market Share Fraction (AUTO-CALCULATED)
   * Formula: soldToMarket / totalProduction
   * @param {number} soldToMarket
   * @param {number} totalProduction
   * @returns {number} Fraction (0-1)
   */
  marketShareFraction(soldToMarket, totalProduction) {
    if (!totalProduction || totalProduction === 0) return 0;
    return round(p(soldToMarket) / p(totalProduction), 4);
  },

  /**
   * D.4 — Material Balance Check
   * Formula: Total = Market + OtherProcesses + NonCBAM + Other → balance must = 0
   * Returns balance value — must be 0 for valid submission
   *
   * @param {number} totalProduction
   * @param {number} soldToMarket
   * @param {Array}  consumedOtherProcesses - Array of { amounts }
   * @param {number} consumedNonCbam
   * @returns {{ balance: number, isValid: boolean, message: string }}
   */
  materialBalanceCheck(totalProduction, soldToMarket, consumedOtherProcesses = [], consumedNonCbam = 0) {
    const total       = p(totalProduction);
    const market      = p(soldToMarket);
    const otherProcs  = consumedOtherProcesses.reduce((s, r) => s + p(r.amounts), 0);
    const nonCbam     = p(consumedNonCbam);
    const balance     = round(total - market - otherProcs - nonCbam, 4);
    const isValid     = Math.abs(balance) < 0.001; // tolerance for floating point

    return {
      balance,
      isValid,
      message: isValid ? '' : `Material balance error: ${balance} (must be 0). Check production vs. allocation.`,
    };
  },

  /**
   * D.5 + D.7 + D.8 + D.9 — Full SEE Calculation
   * Computes SEE_direct, SEE_indirect, SEE_total for one production process.
   *
   * CBAM Implementing Regulation Annex IV:
   *   DirEm_net = DirEm
   *             + (heat_in × EF_heat_in) - (heat_out × EF_heat_out)
   *             + (wg_in × EF_wg_in) - (wg_out × EF_wg_out × 0.667)  ← D.9 fix
   *
   *   SEE_direct   = DirEm_net / totalProduction
   *   Indir_Em     = (elec_consumed × EF_elec) - (elec_exported × EF_elec_out)
   *   SEE_indirect = Indir_Em / totalProduction  (only if indirectEmissions = TRUE)
   *   SEE_total    = SEE_direct + SEE_indirect
   *
   * @param {object} proc - Full process object from FormD state
   * @returns {{ see_direct, see_indirect, see_total, dirEm_net, indir_em }}
   */
  calculateSEE(proc, precursors = []) {
    const totalProd = D.totalProductionVolume(proc.productionLevels || []);

    const heatNet =
      p(proc.measurableHeat?.amountImported)  * p(proc.measurableHeat?.emissionFactorImported)
    - p(proc.measurableHeat?.amountExported)  * p(proc.measurableHeat?.emissionFactorExported);

    const wasteGasNet =
      p(proc.wasteGases?.amountImported)      * p(proc.wasteGases?.emissionFactorImported)
    - p(proc.wasteGases?.amountExported)      * p(proc.wasteGases?.emissionFactorExported)
                                              * CONSTANTS.WASTE_GAS_EXPORT_RATIO;

    const dirEm_net = p(proc.directlyAttributableEmissions) + heatNet + wasteGasNet;

    const denom = totalProd || p(proc.productionLevelsTotal) || 0;
    const SEE_direct_process = denom > 0 ? dirEm_net / denom : 0;

    const indirApplicable = proc.applicableElements?.indirectEmissions === 'TRUE';
    const indir_em = indirApplicable
      ? p(proc.electricityConsumption?.consumption) * p(proc.electricityConsumption?.emissionFactor)
        - p(proc.electricityExported?.amountsExported) * p(proc.electricityExported?.emissionFactor)
      : 0;

    const SEE_indirect_process = denom > 0 ? indir_em / denom : 0;

    // ── PRECURSOR CONTRIBUTION LAYER ─────────────────────────────────────────
    // For each precursor consumed by THIS process, add:
    //   (consumed_mass × precursor_SEE) / production
    const procId   = proc.processId || proc.id;
    const procName = proc.title || proc.name || proc.processName;

    let precDirectSum   = 0;
    let precIndirectSum = 0;

    (precursors || []).forEach((prec) => {
      const consumed = (prec.consumedInProcesses || []).find((cp) =>
        (procId   && (cp.processId === procId   || cp.id === procId))   ||
        (procName && (cp.processName === procName || cp.name === procName ||
                      cp.title === procName       || cp.process === procName))
      );
      if (!consumed) return;

      const consumedMass = p(consumed.amounts || consumed.amount || 0);
      precDirectSum   += consumedMass * p(prec.emissions?.seeDirect);
      precIndirectSum += consumedMass * p(prec.emissions?.seeIndirect);
    });

    const precursorDirectContribution   = denom > 0 ? precDirectSum   / denom : 0;
    const precursorIndirectContribution = denom > 0 ? precIndirectSum / denom : 0;

    const SEE_direct   = SEE_direct_process   + precursorDirectContribution;
    const SEE_indirect = SEE_indirect_process + precursorIndirectContribution;
    const SEE_total    = SEE_direct + SEE_indirect;

    return {
      productionLevelsTotalAuto:     round(totalProd),
      marketShareFraction:           D.marketShareFraction(
                                       proc.productionDetails?.amountsProducedForMarket,
                                       totalProd
                                     ),
      see_direct:                    round(SEE_direct,                    6),
      see_indirect:                  round(SEE_indirect,                  6),
      see_total:                     round(SEE_total,                     6),
      see_direct_process:            round(SEE_direct_process,            6),
      see_indirect_process:          round(SEE_indirect_process,          6),
      precursorDirectContribution:   round(precursorDirectContribution,   6),
      precursorIndirectContribution: round(precursorIndirectContribution, 6),
      dirEm_net:                     round(dirEm_net,                     4),
      indir_em:                      round(indir_em,                      4),
    };
  },
};

// ─── FORMULA NAMESPACE E — Sheet E_PurchPrec ─────────────────────────────────

export const E = {

  /**
   * E.4 — Total Precursor Quantity
   * Sums amounts across all route sub-rows
   */
  totalPrecursorQuantity(levels = []) {
    return round(levels.reduce((s, row) => s + p(row.amounts), 0));
  },

  /**
   * E.6 — Precursor Material Balance Check
   * Total purchased must equal sum distributed to all production processes
   *
   * @param {number} totalPurchased
   * @param {Array}  consumedInProcesses - Array of { amounts }
   * @returns {{ balance: number, isValid: boolean, message: string }}
   */
  materialBalanceCheck(totalPurchased, consumedInProcesses = []) {
    const distributed = consumedInProcesses.reduce((s, r) => s + p(r.amounts), 0);
    const balance     = round(p(totalPurchased) - distributed, 4);
    const isValid     = Math.abs(balance) < 0.001;

    return {
      balance,
      isValid,
      message: isValid
        ? ''
        : `Precursor balance error: ${balance} t unaccounted. Total purchased must equal sum assigned to processes.`,
    };
  },

  /**
   * E.7 — Precursor Direct Embedded Emissions (total)
   * Formula: totalDirectEmissions = quantity × seeDirect (tCO2/t)
   */
  totalDirectEmissions(quantity, seeDirect) {
    return round(p(quantity) * p(seeDirect));
  },

  /**
   * E.8 — Precursor Indirect Embedded Emissions
   * Formula: seeIndirect = secIndirect × eefIndirect
   * Total: quantity × seeIndirect
   */
  seeIndirect(secIndirect, eefIndirect) {
    return round(p(secIndirect) * p(eefIndirect));
  },

  totalIndirectEmissions(quantity, secIndirect, eefIndirect) {
    return round(p(quantity) * E.seeIndirect(secIndirect, eefIndirect));
  },

  /**
   * E.9 — Data Quality Gate
   * Both direct emissions method AND electricity method must be declared
   * Returns true if complete, false if incomplete
   */
  dataQualityGate(directMethod, elecMethod) {
    const unknown = ['', 'Unknown', null, undefined];
    return !unknown.includes(directMethod) && !unknown.includes(elecMethod);
  },

  /**
   * Apply all derived fields to a precursor object
   */
  applyDerivedFields(precursor) {
    const qty = E.totalPrecursorQuantity(precursor.totalPurchasedLevels || []);
    const seeInd = E.seeIndirect(
      precursor.emissions?.secIndirect,
      precursor.emissions?.eefIndirect
    );
    const totalDirectEm  = E.totalDirectEmissions(qty, precursor.emissions?.seeDirect);
    const totalIndirectEm = round(qty * seeInd);
    const balance = E.materialBalanceCheck(qty, precursor.consumedInProcesses || []);
    const dqGate  = E.dataQualityGate(
      precursor.emissions?.sourceSeeDirect,
      precursor.emissions?.eefIndirectSource
    );

    return {
      ...precursor,
      totalPurchaseConsumption:    String(qty),
      _materialBalanceCheck:       balance,
      _dataQualityComplete:        dqGate,
      emissions: {
        ...precursor.emissions,
        seeIndirect:          String(seeInd),
        totalDirectEmissions: String(totalDirectEm),
        totalIndirectEmissions: String(totalIndirectEm),
      },
    };
  },
};

// ─── FORMULA NAMESPACE F — Sheet F_Tools ─────────────────────────────────────

export const F = {

  /**
   * F.1 — MWh to TJ Conversion
   * Formula: TJ = MWh × 3.6 / 1000 = MWh × 0.0036
   */
  mwhToTJ(mwh) {
    return round(p(mwh) * CONSTANTS.MWH_TO_TJ);
  },

  /**
   * F.2 — CHP Heat Attribution Factor (AUTO-CALCULATED)
   * Formula: factor = heatOutput / fuelInput
   * Falls back to default 0.55 if data unavailable
   *
   * @param {number} heatOutput  - Heat output (TJ)
   * @param {number} fuelInput   - Total fuel input (TJ)
   * @param {number} elecOutput  - Electricity output (TJ)
   * @returns {{ heatFactor, elecFactor }}
   */
  chpAttributionFactors(heatOutput, fuelInput, elecOutput) {
    if (!fuelInput || p(fuelInput) === 0) {
      return {
        heatFactor: CONSTANTS.CHP_HEAT_ATTR_DEFAULT,
        elecFactor: CONSTANTS.CHP_ELEC_ATTR_DEFAULT,
      };
    }
    return {
      heatFactor: round(p(heatOutput) / p(fuelInput), 4),
      elecFactor: round(p(elecOutput) / p(fuelInput), 4),
    };
  },

  /**
   * F.3 — CHP Specific Emission Factor for Heat
   * Formula: EF_heat = (heatFactor × totalEmissions) / heatEfficiency
   * heatEfficiency default = 0.88 (88%)
   *
   * @param {number} heatFactor        - Heat attribution factor from F.2
   * @param {number} totalCHPEmissions - Total CHP emissions (tCO2)
   * @param {number} heatEfficiency    - Reference heat efficiency (default 0.88)
   * @returns {number} Specific EF for heat (tCO2/TJ)
   */
  chpHeatSpecificEF(heatFactor, totalCHPEmissions, heatEfficiency = CONSTANTS.CHP_HEAT_DEFAULT_EFF) {
    if (!heatEfficiency || p(heatEfficiency) === 0) return 0;
    return round((p(heatFactor) * p(totalCHPEmissions)) / p(heatEfficiency), 6);
  },

  /**
   * F.4 — CHP Fuel Split Between Heat and Electricity
   * Formula: heatShare = (heat/heatEff) / (heat/heatEff + elec/elecEff) × totalFuel
   *
   * @param {number} heatOutput  - Heat output (TJ)
   * @param {number} elecOutput  - Electricity output (TJ)
   * @param {number} totalFuel   - Total fuel input (TJ)
   * @param {number} heatEff     - Heat efficiency (default 0.88)
   * @param {number} elecEff     - Electricity efficiency (default 0.442)
   * @returns {{ heatFuel, elecFuel }} Fuel split in TJ
   */
  chpFuelSplit(
    heatOutput, elecOutput, totalFuel,
    heatEff = CONSTANTS.CHP_HEAT_DEFAULT_EFF,
    elecEff = CONSTANTS.CHP_ELEC_DEFAULT_EFF
  ) {
    const heatTerm = p(heatOutput) / (p(heatEff) || 1);
    const elecTerm = p(elecOutput) / (p(elecEff) || 1);
    const total    = heatTerm + elecTerm;

    if (total === 0) return { heatFuel: 0, elecFuel: 0 };

    const heatFuel = (heatTerm / total) * p(totalFuel);
    const elecFuel = p(totalFuel) - heatFuel;

    return {
      heatFuel: round(heatFuel),
      elecFuel: round(elecFuel),
    };
  },

  /**
   * F.5 + F.6 + F.7 + F.9 — Carbon Price Calculations
   * All carbon price formulas for one process row
   *
   * @param {object} row
   * @returns {object} Row with calculated fields
   */
  applyCaronPriceCalculations(row) {
    const se    = p(row.se_total_tCO2e_t);
    const share = p(row.share_of_emissions_covered);
    const price = p(row.carbon_price_due_local);
    const rebateRate = p(row.amount_of_rebate_local);

    // F.6: Total CBAM emissions subject to obligation
    const coveredSe = se * share;                                    // tCO2e/t covered

    // F.7: Carbon price due
    const cpDue     = coveredSe * price;

    // Rebate
    const rebate    = coveredSe * rebateRate;

    // F.9: Net CBAM cost
    const effectiveCp = cpDue - rebate;

    const fmt = (v) => v === 0 ? '' : String(round(v, 6));

    return {
      ...row,
      covered_se_tCO2e_t:           fmt(coveredSe),
      cp_due_per_produced_t_or_MWh: fmt(cpDue),
      rebate_per_produced_t_or_MWh: fmt(rebate),
      result_effective_cp:          fmt(effectiveCp),
    };
  },
};

// ─── VALIDATION HELPERS ───────────────────────────────────────────────────────

export const validate = {
  /**
   * Check if a value is a valid positive number
   */
  isPositiveNumber: (v) => Number.isFinite(p(v)) && p(v) > 0,

  /**
   * Row status class for UI highlighting
   * Returns 'error' | 'warning' | 'ok' | 'empty'
   */
  rowStatusClass(row) {
    if (!row.method) return 'empty';
    if (row._validationStatus === 'inconsistent') return 'error';
    if (row._validationStatus === 'incomplete')   return 'warning';
    return 'ok';
  },
};

//  DEFAULT EXPORT 

export default { B, C, D, E, F, CONSTANTS, validate };
